import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, LOCALE_ID, OnInit, inject, signal } from "@angular/core";
import { StoreUserService } from '@app/core/state/store-user.service';
import {
  DatabaseCacheService,
  Section,
  Breadcrumb,
} from "../../../data/cache/database-cache.service";
import { MsalService, MsalBroadcastService } from "@azure/msal-angular";
import { StoreAuthService } from '@app/core/auth/store-auth.service';
import { ActivatedRoute, Router } from "@angular/router";
import { Validators } from "@angular/forms";
import { FormBuilder } from "@angular/forms";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

import {
  RecordTypes,
  FileTypes as fileTypes,
  getRecordTypeDisplay,
  resolveRecordTypeLocale,
  RecordTypeLocale,
} from "../../../types";
import { SHARED_IMPORTS } from "src/app/shared/standalone-imports";

@Component({
  selector: "app-records-download-request-list",
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./records-download-request-list.component.html",
  styleUrls: ["./records-download-request-list.component.scss"],
  imports: [...SHARED_IMPORTS],
})
export class RecordsDownloadRequestComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly isEnglish = (inject(LOCALE_ID) || '').toLowerCase().startsWith('en');

  // Loading state
  isLoading = signal(true);

  private toolbarTitle = this.isEnglish ? "Record Data Download" : "レコードデータダウンロード";

  messages = {};
  responseData: any = [];
  createDialogResData: any = [];
  selectedSection: Section;

  selectedUserId;
  selectedUserWithUserData;

  downloadRequests;

  accountManagementRole: boolean = false;
  userManagementRole: boolean = false;
  recordMeasurerRole: boolean = false;
  recordRefererRole: boolean = false;

  primaryLocale = navigator.languages[0];
  recordTypeLocale: RecordTypeLocale = resolveRecordTypeLocale();

  constructor(
    public storeUser: StoreUserService,
    private storeAuth: StoreAuthService,
    private msalService: MsalService,
    private route: ActivatedRoute,
    private common: DatabaseCacheService,
    private fb: FormBuilder,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.common.parametersUpdated.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      ////// Sectionの選択と関連データの更新が終了したので、データベースキャッシュの必要なデータをローカル変数に読み込む

      this.selectedSection = this.common.getSelectedSection();

      this.downloadRequests = this.common.getRecordsDownloadRequestsList();
      if (this.downloadRequests != null) {
        this.downloadRequests.forEach((r) => {
          r.createdAt = new Date(r.createdAt).toLocaleString(
            this.primaryLocale,
            {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            },
          );
          r.validTo = new Date(r.validTo).toLocaleString(this.primaryLocale, {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          });
        });
      }
      this.isLoading.set(false);
      this.cdr.markForCheck();
    });

    // route.params.subscribe への Subscribe は this.common.parametersUpdated への Subscribe よりあとに行うこと
    this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      // 現在のSection内のRoleを取得し、権限があればメディアのリストを取得する
      const roles = this.common.getSelectedSectionRoles();
      this.determineRoles(roles);
      if (this.recordMeasurerRole || this.recordRefererRole) {
        this.common.prepareRecordsDownloadRequestsList(false);
      }
    });
  }

  determineRoles(roles) {
    this.accountManagementRole = false;
    this.userManagementRole = false;
    this.recordMeasurerRole = false;
    this.recordRefererRole = false;

    if (roles != null && roles.length != 0) {
      if (roles.indexOf("ACCOUNT_MANAGEMENT") !== -1) {

        this.accountManagementRole = true;
      }

      if (roles.indexOf("USER_MANAGEMENT") !== -1) {

        this.userManagementRole = true;
      }

      if (roles.indexOf("MEASUREMENT") !== -1) {

        this.recordMeasurerRole = true;
      }

      if (roles.indexOf("REFER") !== -1) {

        this.recordRefererRole = true;
      }
    } else {
    }
  }

  refreshData() {
    this.isLoading.set(true);
    this.common.prepareRecordsDownloadRequestsList(true);
  }

  createNewDownloadRequest() {
    this.router.navigate([
      "/section-admin/",
      this.selectedSection.sectionId,
      "create-records-download-requests",
    ]);
  }

  getConditions(request) {
    const i18n = this.isEnglish
      ? {
          period: 'Period: ',
          from: 'from',
          to: 'to',
          allUsers: 'All users,',
          users: 'Users',
          allRecordTypes: 'All record types, ',
          recordTypes: 'Record types',
          allFileTypes: 'All data types, ',
          fileTypes: 'Data types',
        }
      : {
          period: '期間: ',
          from: 'から',
          to: 'まで',
          allUsers: 'すべてのユーザー,',
          users: 'ユーザー',
          allRecordTypes: 'すべてのレコードタイプ, ',
          recordTypes: 'レコードタイプ',
          allFileTypes: 'すべてのデータ種別, ',
          fileTypes: 'データ種別',
        };

    let conditions = "";

    if (
      request.conditions.fromDate != null ||
      request.conditions.toDate != null
    ) {
      conditions += i18n.period;
      if (request.conditions.fromDate != null) {
        let fd = new Date(request.conditions.fromDate).toLocaleString(
          this.primaryLocale,
          { year: "numeric", month: "2-digit", day: "2-digit" },
        );
        conditions += `${fd} ${i18n.from}`;
      }
      if (request.conditions.toDate != null) {
        let td = new Date(request.conditions.toDate).toLocaleString(
          this.primaryLocale,
          { year: "numeric", month: "2-digit", day: "2-digit" },
        );
        conditions += `${td} ${i18n.to}`;
      }
      conditions += ", ";
    }

    if (request.conditions.userIds.length == 0) {
      conditions += i18n.allUsers;
    } else {
      conditions += `${i18n.users}:${JSON.stringify(request.conditions.userIds)}, `;
    }

    if (
      request.conditions.recordTypes == null ||
      request.conditions.recordTypes.length == 0
    ) {
      conditions += i18n.allRecordTypes;
    } else {
      let recordConditionDispValues = Array.from(
        new Set(
          RecordTypes.filter((r) =>
            request.conditions.recordTypes.includes(r.code),
          ).map((r) => getRecordTypeDisplay(r.code, this.recordTypeLocale)),
        ),
      );
      conditions += `${i18n.recordTypes}:${JSON.stringify(recordConditionDispValues)}, `;
    }

    if (
      request.conditions.fileTypes == null ||
      request.conditions.fileTypes.length == 0
    ) {
      conditions += i18n.allFileTypes;
    } else {
      let conditionFileTypes = Array.from(
        new Set(
          fileTypes
            .filter((r) => request.conditions.fileTypes.includes(r.value))
            .map((r) => r.display),
        ),
      );
      conditions += `${i18n.fileTypes}:${JSON.stringify(conditionFileTypes)}, `;
    }

    conditions = conditions.endsWith(", ")
      ? conditions.slice(0, -2)
      : conditions;

    return conditions;
  }

  getFilename(url) {
    try {
      // URLオブジェクトを作成
      const urlObj = new URL(url);

      // パスからファイル名を取り出す
      const pathSegments = urlObj.pathname.split("/");
      const fileName = pathSegments.pop() || ""; // ファイル名を取得

      return fileName;
    } catch (error) {
      console.error(this.isEnglish ? "Failed to parse URL:" : "URLの解析に失敗しました:", error);
      return null;
    }
  }

  getSelectedUserId() {
    //return this.common.selectedUserId;
    return this.common.getSelectedUserId();
  }
}
